import React from "react";

/** Portrait + name; optional website link. `size` grid → platinum layout. */
export function SponsorPersonCard({ entry, size = "platinum", bulandi = false }) {
  const { name, imageUrl, websiteUrl, blankPlaceholder } = entry;
  const label = name?.trim() ? name.trim() : "To be announced";
  const tier = size === "grid" ? "platinum" : size;
  const isTitle = tier === "title";
  const isPlatinum = tier === "platinum";
  const isGold = tier === "gold";
  const isAdvisor = tier === "advisor";

  /** Platinum: square stage; gold/advisor: smaller 4:3 cap; title: large 4:3. No frames or borders. */
  const stageClass = isTitle
    ? "relative mx-auto w-full max-w-[min(100%,320px)] shrink-0 aspect-[4/3]"
    : isPlatinum
      ? "relative w-full min-w-0 shrink-0 aspect-square max-w-full"
      : "relative w-full shrink-0 aspect-[4/3]";

  const imgRadius = isTitle ? "rounded-lg sm:rounded-xl" : "rounded-md sm:rounded-lg";
  const innerBg = bulandi ? "bg-bul-bg" : "bg-zinc-950";
  const altRole = isAdvisor ? "advisor" : "sponsor";

  const media = imageUrl?.trim() ? (
    <img
      src={imageUrl.trim()}
      alt={name?.trim() ? `${name.trim()} (${altRole})` : isAdvisor ? "Advisor" : "Sponsor"}
      className={`max-h-full max-w-full object-contain object-center ${imgRadius} ${innerBg}`}
    />
  ) : blankPlaceholder ? (
    <div
      className={`h-full w-full min-h-[4.5rem] ${imgRadius} ${innerBg} ${
        bulandi ? "border border-bul-border/50" : "border border-zinc-700/50"
      }`}
      aria-hidden
    />
  ) : (
    <div
      className={`flex h-full min-h-0 w-full flex-col items-center justify-center px-2 text-center ${imgRadius} ${innerBg} ${
        bulandi ? "text-bul-mutedfg" : "text-zinc-500"
      }`}
    >
      <span className="font-mono text-[10px] font-bold uppercase tracking-wider sm:text-xs">
        Image coming soon
      </span>
    </div>
  );

  const nameClass = bulandi
    ? isTitle
      ? "text-[0.75rem] text-bul-amber sm:text-[0.84375rem] lg:text-[0.9375rem]"
      : isGold || isAdvisor
        ? "text-[9px] text-bul-amber sm:text-[10px]"
        : "text-[10px] text-bul-amber sm:text-xs"
    : isTitle
      ? "text-base text-amber-200 sm:text-lg lg:text-xl"
      : isGold
        ? "text-[10px] text-amber-200 sm:text-xs"
        : isAdvisor
          ? "text-[10px] text-amber-200 sm:text-xs"
          : "text-xs text-amber-200 sm:text-sm";

  const nameBlockClass = isTitle
    ? `mt-2.5 block w-full shrink-0 px-1 text-center font-display font-bold uppercase leading-snug tracking-wide sm:mt-3 min-h-[2.75rem] sm:min-h-[3.25rem] ${nameClass}`
    : isGold || isAdvisor
      ? `mt-1.5 block w-full shrink-0 px-0.5 text-center font-display font-bold uppercase leading-snug tracking-wide sm:mt-2 min-h-[2rem] sm:min-h-[2.25rem] ${nameClass}`
      : `mt-2 block w-full shrink-0 px-0.5 text-center font-display font-bold uppercase leading-snug tracking-wide sm:mt-2.5 min-h-[2.5rem] sm:min-h-[2.875rem] ${nameClass}`;

  const inner = (
    <div
      className={`flex min-h-0 w-full min-w-0 flex-col ${
        isTitle
          ? "mx-auto max-w-sm items-stretch"
          : isGold || isAdvisor
            ? "mx-auto w-full max-w-[9rem] items-stretch sm:max-w-[10.25rem]"
            : bulandi && isPlatinum
              ? "mx-auto w-full max-w-[12rem] items-stretch sm:max-w-[13.5rem]"
              : "items-stretch"
      }`}
    >
      <div className={stageClass}>
        <div className="flex h-full min-h-0 w-full min-w-0 items-center justify-center overflow-hidden">
          {media}
        </div>
      </div>
      <p className={`${nameBlockClass} line-clamp-2`}>{label}</p>
    </div>
  );

  const rootCenterClass =
    bulandi && (isTitle || isPlatinum || isGold || isAdvisor) ? "w-full items-center" : "";

  if (websiteUrl?.trim()) {
    return (
      <a
        href={websiteUrl.trim()}
        target="_blank"
        rel="noopener noreferrer"
        className={`flex h-full min-h-0 flex-col outline-offset-2 transition hover:opacity-90 focus-visible:ring-2 ${
          bulandi ? "focus-visible:ring-bul-lime" : "focus-visible:ring-amber-400"
        } ${rootCenterClass}`}
      >
        {inner}
      </a>
    );
  }

  return (
    <div className={`flex h-full min-h-0 flex-col ${rootCenterClass}`}>{inner}</div>
  );
}
